"use client";

import {
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from "@scott/contentful/live-preview-init";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, useTransition } from "react";
import {
  ProductListingBlockClient,
  type ProductListingBlockClientProps,
} from "@/components/blocks/content-blocks/product-listing-block.client";

export type ProductListingLiveEntry = {
  __typename: "ProductListing";
  sys?: { id: string };
  internalName: string;
  title?: string;
  anchorId?: string;
  productCategory?: string;
  defaultSort?: string;
  searchQuery?: string;
  showFilters?: boolean;
  showSorting?: boolean;
  paginationMode?: "paginate" | "showAll";
  pageSize?: number;
};

type Props = ProductListingBlockClientProps & {
  liveEntry: ProductListingLiveEntry;
};

const EDITOR_REFRESH_DEBOUNCE_MS = 1000;
// Extra delay before retrying when the first refresh returned stale data.
// Gives Contentful's auto-save enough time to commit the draft.
const STALE_RETRY_DELAY_MS = 1500;

const shimmerKeyframes = `
@keyframes plp-preview-shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
`;

type DisplayFieldDescriptor = {
  field: keyof ProductListingLiveEntry;
  // Override the uiProps key when it differs from the field name (e.g. showSorting → showSort).
  uiKey?: string;
  // true: also writes to adapterContext (title, anchorId — used by the section anchor/heading).
  toAdapter?: boolean;
  // true: also writes to adapterContext.presentation (controls rendering behavior like filters/sorting visibility).
  toPresentation?: boolean;
};

// Display-only fields: applied immediately as overrides, no server refresh needed.
// Commerce fields: require router.refresh() + product-API round-trip (shimmer shown).
// To add a new field: put it in the right array — that's the only place to touch.
const DISPLAY_FIELDS: readonly DisplayFieldDescriptor[] = [
  { field: "title", toAdapter: true },
  { field: "anchorId", toAdapter: true },
  { field: "showFilters", toPresentation: true },
  { field: "showSorting", toPresentation: true, uiKey: "showSort" },
  { field: "paginationMode", toPresentation: true },
];

const COMMERCE_FIELDS: ReadonlyArray<keyof ProductListingLiveEntry> = [
  "productCategory",
  "defaultSort",
  "searchQuery",
  "pageSize",
];

function buildLiveOverrides(
  updated: ProductListingLiveEntry,
  server: ProductListingLiveEntry
) {
  const uiProps: Partial<ProductListingBlockClientProps["uiProps"]> = {};
  const presentation: Partial<
    ProductListingBlockClientProps["adapterContext"]["presentation"]
  > = {};
  const adapter: { title?: string; anchorId?: string } = {};

  for (const { field, uiKey, toAdapter, toPresentation } of DISPLAY_FIELDS) {
    if (updated[field] === server[field]) {
      continue;
    }
    const value = updated[field];
    (uiProps as Record<string, unknown>)[uiKey ?? field] = value;
    if (toAdapter) {
      (adapter as Record<string, unknown>)[field] = value;
    }
    if (toPresentation) {
      (presentation as Record<string, unknown>)[field] = value;
    }
  }

  const hasPresentationChange = Object.keys(presentation).length > 0;
  const hasAnyChange = hasPresentationChange || Object.keys(uiProps).length > 0;

  if (!hasAnyChange) {
    return;
  }

  return { uiProps, presentation, adapter, hasPresentationChange };
}

function hasCommerceChange(
  updated: ProductListingLiveEntry,
  server: ProductListingLiveEntry
): boolean {
  return COMMERCE_FIELDS.some((field) => updated[field] !== server[field]);
}

export function ProductListingBlockClientPreview(props: Readonly<Props>) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isShimmering, setIsShimmering] = useState(false);

  const isPendingRef = useRef(false);
  isPendingRef.current = isPending;

  const updatedEntry = useContentfulLiveUpdates(props.liveEntry);
  const prevEntryRef = useRef(props.liveEntry);

  // Always holds the latest server-delivered liveEntry so the effect can
  // compare against current server state without a stale closure.
  // Updated synchronously during render (before effects run).
  const liveEntryRef = useRef(props.liveEntry);
  liveEntryRef.current = props.liveEntry;

  const inspector = useContentfulInspectorMode({
    entryId: props.adapterContext.sysId,
  });

  // router.refresh and startTransition are stable references — safe to omit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only re-run on editor updates
  useEffect(() => {
    if (updatedEntry === prevEntryRef.current) {
      return;
    }

    // Refresh in flight: sync the ref only — don't start another shimmer cycle.
    if (isPendingRef.current) {
      prevEntryRef.current = updatedEntry;
      return;
    }

    prevEntryRef.current = updatedEntry;

    // Display-only fields (title, anchorId, showFilters, showSorting, paginationMode)
    // are applied immediately via uiPropsOverrides — no shimmer needed for them.
    if (!hasCommerceChange(updatedEntry, liveEntryRef.current)) {
      return;
    }

    setIsShimmering(true);
    const timer = setTimeout(() => {
      if (!isPendingRef.current) {
        startTransition(() => router.refresh());
      }
    }, EDITOR_REFRESH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [updatedEntry]);

  const remountKey = JSON.stringify({
    queryContext: props.queryContext,
    presentation: props.adapterContext.presentation,
    uiConfig: props.adapterContext.uiConfig,
    title: props.adapterContext.title,
    anchorId: props.adapterContext.anchorId,
    defaultQueryState: props.defaultQueryState,
    listingId: props.listingId,
  });

  // Remembers the remountKey from the last successful shimmer cycle so we can
  // detect whether the server actually returned new data after a refresh.
  // For text fields (searchQuery etc.) Contentful's auto-save may take longer
  // than the debounce window — in that case the first refresh returns stale data.
  // The shimmer stays visible and the retry timer fires until the key changes.
  const lastRemountKeyRef = useRef(remountKey);

  // While shimmering and idle: check if new server data arrived (remountKey
  // changed) and clear the shimmer, or schedule another refresh if data is
  // still stale (Contentful auto-save not committed yet).
  // biome-ignore lint/correctness/useExhaustiveDependencies: startTransition and router are stable — safe to omit
  useEffect(() => {
    if (!isShimmering || isPending) {
      return;
    }

    if (remountKey !== lastRemountKeyRef.current) {
      lastRemountKeyRef.current = remountKey;
      setIsShimmering(false);
      return;
    }

    const timer = setTimeout(() => {
      startTransition(() => router.refresh());
    }, STALE_RETRY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isShimmering, isPending, remountKey]);

  const liveOverrides = buildLiveOverrides(updatedEntry, props.liveEntry);

  const effectiveAdapterContext = liveOverrides
    ? {
        ...props.adapterContext,
        ...liveOverrides.adapter,
        presentation: liveOverrides.hasPresentationChange
          ? {
              ...props.adapterContext.presentation,
              ...liveOverrides.presentation,
            }
          : props.adapterContext.presentation,
      }
    : props.adapterContext;

  return (
    <div className="relative">
      {isShimmering && (
        <>
          <style>{shimmerKeyframes}</style>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-50 overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-linear-to-r from-transparent via-gray-500/65 to-transparent"
              style={{ animation: "plp-preview-shimmer 1.5s infinite" }}
            />
          </div>
        </>
      )}
      <ProductListingBlockClient
        {...props}
        adapterContext={effectiveAdapterContext}
        inspectorProps={{
          container: inspector({ fieldId: "internalName" }) ?? undefined,
        }}
        key={remountKey}
        uiPropsOverrides={liveOverrides?.uiProps}
      />
    </div>
  );
}
